import { Controller } from "@hotwired/stimulus"
import ContactsImportService from "../services/contacts_import_service"

/**
 * Contacts Import Controller (Refactored)
 * 
 * Orchestrates UX flow only:
 * - Modal display/hide
 * - Button title updates
 * - Cookie management
 * 
 * Delegates to:
 * - flutter_permissions_controller for permissions
 * - contacts_import_service for import business logic
 */
export default class extends Controller {
    static targets = ["modal", "importButton", "laterButton", "button", "title"]
    static values = {
        apiUrl: {type: String, default: "/api/v1/contacts/import"},
        storageKey: {type: String, default: "contacts_permission_modal_dismissed"},
        daysToHide: {type: Number, default: 10},
        autoImport: {type: Boolean, default: false} // true for auto-import on sign-in
    }

    static SESSION_KEY = "contacts_imported_this_session"
    static COOKIE_NAME = "contacts_last_import_at"
    static DISMISSAL_COOKIE_NAME = "contacts_permission_modal_dismissed"
    static DISMISSAL_COOKIE_DAYS = 14
    static DAILY_IMPORT_COOKIE_HOURS = 24
    static MAX_WAIT_ATTEMPTS = 20
    static WAIT_INTERVAL_MS = 250

    connect() {
        // Hide button if not in mobile app (for settings page)
        if (!this.isInApp && this.hasButtonTarget) {
            this.buttonTarget.style.display = "none"
        }

        // Auto-import mode: check if we should show modal on first connection
        if (this.autoImportValue) {
            this.handleAutoImport()
        }
    }

    /**
     * Check if we're in Flutter app
     */
    get isInApp() {
        return window.FlutterPermissions?.isInApp || false
    }

    /**
     * Handle auto-import on sign-in (first connection)
     */
    handleAutoImport() {
        // Check if contacts were already imported this session
        if (sessionStorage.getItem(this.constructor.SESSION_KEY) === "true") {
            this.hideModal()
            return
        }

        // If not in mobile app, hide modal immediately
        if (!this.isInApp) {
            this.hideModal()
            return
        }

        // Wait for FlutterPermissions to be ready
        this.waitForFlutterPermissions()
    }

    /**
     * Wait for FlutterPermissions helper to be available
     */
    waitForFlutterPermissions(attempts = 0) {
        if (window.FlutterPermissions) {
            this.initializeAutoImport()
            return
        }

        // If we've waited too long, give up
        if (attempts >= this.constructor.MAX_WAIT_ATTEMPTS) {
            this.hideModal()
            return
        }

        // Wait and try again
        setTimeout(() => {
            this.waitForFlutterPermissions(attempts + 1)
        }, this.constructor.WAIT_INTERVAL_MS)
    }

    /**
     * Initialize auto-import flow
     */
    initializeAutoImport() {
        // Check permission status via FlutterPermissions
        const hasPermission = window.FlutterPermissions.hasPermission("contacts")
        const dismissalCookieExists = this.getDismissalCookie() !== null

        // Modal display logic: show ONLY if dismissal cookie NOT present OR permission NOT granted
        // If cookie EXISTS AND permission GRANTED → NEVER display modal
        if (dismissalCookieExists && hasPermission) {
            // Cookie exists and permission granted - never show modal
            this.hideModal()
            // Check if daily import is needed
            if (this.shouldImportToday()) {
                this.startSilentImport()
            }
        } else if (!hasPermission) {
            // Permission not granted - check if modal should be shown
            if (this.shouldShowModal()) {
                this.showModal()
            } else {
                this.hideModal()
            }
        } else {
            // Permission granted but no dismissal cookie - run daily import if needed
            if (this.shouldImportToday()) {
                this.startSilentImport()
            }
            this.hideModal()
        }
    }

    /**
     * Determine if modal should be shown
     */
    shouldShowModal() {
        // Check if dismissal cookie exists
        const dismissalCookie = this.getDismissalCookie()
        if (dismissalCookie !== null) {
            // Cookie exists - check if still valid (14 days)
            const cookieDate = new Date(dismissalCookie)
            const daysSinceDismissed = Math.floor((Date.now() - cookieDate.getTime()) / (1000 * 60 * 60 * 24))

            // If less than 14 days, don't show
            if (daysSinceDismissed < this.constructor.DISMISSAL_COOKIE_DAYS) {
                return false
            }
            // Cookie expired - can show modal again
        }

        // Check if permission was previously denied
        if (window.FlutterPermissions && window.FlutterPermissions.isDenied("contacts")) {
            return false
        }

        return true
    }

    /**
     * Get dismissal cookie value
     * @returns {string|null} - Cookie value or null if not found
     */
    getDismissalCookie() {
        const cookies = document.cookie.split(';')
        const cookie = cookies.find(c => c.trim().startsWith(`${this.constructor.DISMISSAL_COOKIE_NAME}=`))
        
        if (!cookie) {
            return null
        }

        const value = cookie.split('=')[1]?.trim()
        return value || null
    }

    /**
     * Set dismissal cookie with 14-day expiration
     * Only creates cookie if it doesn't already exist
     */
    setDismissalCookie() {
        // Check if cookie already exists
        if (this.getDismissalCookie() !== null) {
            // Cookie exists - do NOT update or extend it
            return
        }

        // Create cookie with 14-day expiration
        const expires = new Date()
        expires.setDate(expires.getDate() + this.constructor.DISMISSAL_COOKIE_DAYS)
        
        const cookieValue = new Date().toISOString()
        document.cookie = `${this.constructor.DISMISSAL_COOKIE_NAME}=${cookieValue}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`
    }

    /**
     * Show the modal
     */
    showModal() {
        if (this.hasModalTarget) {
            this.modalTarget.classList.remove("hidden")
        }
    }

    /**
     * Hide the modal
     */
    hideModal() {
        if (this.hasModalTarget) {
            this.modalTarget.classList.add("hidden")
        }
    }

    /**
     * Handle "Plus tard" button click
     */
    dismissLater(event) {
        event.preventDefault()

        // Set dismissal cookie (only if doesn't exist)
        this.setDismissalCookie()

        // Close modal
        this.hideModal()

        // Do NOT request permission
        // Do NOT import contacts
    }

    /**
     * Handle "Autoriser l'import" button click (from modal)
     */
    async allowImport(event) {
        event.preventDefault()

        // Hide modal first
        this.hideModal()

        // Request permission EXPLICITLY via Flutter
        const permissionResult = await window.FlutterPermissions.requestContacts()

        if (permissionResult.granted) {
            // CASE A - Permission GRANTED: trigger manual import immediately
            // Manual import ignores dismissal cookies
            await this.startImport()
        } else {
            // CASE B - Permission DENIED: apply dismissal cookie logic
            // If dismissal cookie DOES NOT exist: create it with 14-day expiration
            // If it already exists: do NOTHING (do not reset expiration)
            this.setDismissalCookie()
            // Do NOT import contacts
            // Do NOT show any error or message
        }
    }

    /**
     * Manual import from settings page
     * Manual import must be triggerable at any time and ignore dismissal cookies
     */
    async import(event) {
        event.preventDefault()

        if (!this.isInApp) {
            return // Button should be hidden, but just in case
        }

        // Close modal immediately if open
        this.hideModal()

        // Trigger native Flutter contacts permission request
        const permissionResult = await window.FlutterPermissions.requestContacts()

        if (permissionResult.granted) {
            // CASE A - Permission GRANTED: trigger manual import immediately
            // Manual import ignores any auto-import or dismissal cookies
            await this.startImport()
        } else {
            // CASE B - Permission DENIED: apply dismissal cookie logic
            // If dismissal cookie DOES NOT exist: create it with 14-day expiration
            // If it already exists: do NOTHING (do not reset expiration)
            this.setDismissalCookie()
            // Do NOT import contacts
            // Do NOT show any error or message
        }
    }

    /**
     * Start import (for manual import from settings)
     */
    async startImport() {
        // Store original title
        if (this.hasTitleTarget) {
            const baseTitle = this.titleTarget.dataset.baseTitle || this.titleTarget.textContent.trim()
            this.titleTarget.dataset.baseTitle = baseTitle
        }

        try {
            // Use service to import
            const result = await ContactsImportService.import(this.apiUrlValue)

            if (result.status === "processing") {
                // Background job started - update title with message from backend
                if (this.hasTitleTarget && result.message) {
                    this.titleTarget.textContent = result.message
                }
                // Set cookie when processing starts
                this.setLastImportDateCookie()
                return
            }

            if (result.status === "success") {
                // Import completed - set cookie and reload page to show updated title
                this.setLastImportDateCookie()
                // Reload to show updated title with date from cookie
                window.location.reload()
            } else if (result.status === "error") {
                // Error occurred - restore original title
                if (this.hasTitleTarget && this.titleTarget.dataset.baseTitle) {
                    this.titleTarget.textContent = this.titleTarget.dataset.baseTitle
                }
            }
        } catch (error) {
            console.error("[ContactsImport] Import error:", error)
            // Restore original title on error
            if (this.hasTitleTarget && this.titleTarget.dataset.baseTitle) {
                this.titleTarget.textContent = this.titleTarget.dataset.baseTitle
            }
        }
    }

    /**
     * Check if import should be done today (once per day)
     * @returns {boolean} - true if import should be done, false if already done today
     */
    shouldImportToday() {
        const lastImportDate = this.getLastImportDateFromCookie()
        
        if (!lastImportDate) {
            // No cookie - import needed
            return true
        }

        // Check if last import was today
        const today = new Date()
        const lastImport = new Date(lastImportDate)
        
        // Compare dates (year, month, day only)
        const isToday = 
            today.getFullYear() === lastImport.getFullYear() &&
            today.getMonth() === lastImport.getMonth() &&
            today.getDate() === lastImport.getDate()

        // If already imported today, don't import again
        return !isToday
    }

    /**
     * Get last import date from cookie
     * @returns {string|null} - ISO string date or null
     */
    getLastImportDateFromCookie() {
        const cookies = document.cookie.split(';')
        const cookie = cookies.find(c => c.trim().startsWith(`${this.constructor.COOKIE_NAME}=`))
        
        if (!cookie) {
            return null
        }

        const value = cookie.split('=')[1]
        return value || null
    }

    /**
     * Start silent import (for auto-import on sign-in)
     */
    async startSilentImport() {
        // Check if already imported this session
        if (sessionStorage.getItem(this.constructor.SESSION_KEY) === "true") {
            return
        }

        // Check if already imported today (double check)
        if (!this.shouldImportToday()) {
            return
        }

        // Mark as imported IMMEDIATELY to prevent duplicate imports
        sessionStorage.setItem(this.constructor.SESSION_KEY, "true")

        try {
            // Use service to import silently
            const result = await ContactsImportService.import(this.apiUrlValue)

            if (result.status === "success" || result.status === "processing") {
                // Set cookie when import starts/completes
                this.setLastImportDateCookie()
            }
        } catch (error) {
            // Silently handle errors in auto-import mode
            console.warn("[ContactsImport] Silent import error:", error)
        }
    }

    /**
     * Set last import date cookie (for daily import tracking)
     * Uses 24-hour expiration
     */
    setLastImportDateCookie(date = new Date()) {
        const expires = new Date()
        expires.setHours(expires.getHours() + this.constructor.DAILY_IMPORT_COOKIE_HOURS) // 24 hours
        
        const cookieValue = date.toISOString()
        document.cookie = `${this.constructor.COOKIE_NAME}=${cookieValue}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`
    }
}
